import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Input, Button, message } from 'antd';
import { handleApiError } from '../utils/errorHandler';
import {
  PageContainer,
  Card,
  PageTitle,
  FormContainer,
  StyledInput,
  StyledPassword,
  LinksContainer,
  StyledLink,
  ErrorMessage
} from '../styles/SharedStyles';
import { useLanguage } from '../contexts/LanguageContext';
import en from '../locales/en';
import zh from '../locales/zh';

// 验证码按钮样式
const CodeButton = styled(Button)`
  width: 120px !important;
  height: 40px;
  font-size: 16px;
  
  @media (max-width: 480px) {
    height: 44px;
    font-size: 15px;
    width: 110px !important;
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: rgba(255, 255, 255, 0.5) !important;
    border: none !important;
  }
  
  &.ant-btn-loading {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: rgba(255, 255, 255, 0.5) !important;
    border: none !important;
  }
`;

// 验证码输入区域容器
const CodeInputContainer = styled.div`
  display: flex;
  gap: 16px;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

function ResetPassword() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = currentLanguage === 'en' ? en : zh;

  const handleSendCode = async () => {
    if (!email) {
      message.error(t.auth.resetPassword.emailRequired);
      return;
    }
    setSendingCode(true);
    setErrorMessage('');
    
    try {
      const response = await fetch('https://api.kalimbaflows.com/auth/send-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, type: 'reset' }),
      });

      const data = await response.json();
      console.log('发送验证码响应:', data);

      if (!response.ok) {
        const error = new Error(data.message || t.auth.resetPassword.sendCodeFailed);
        const errorMsg = handleApiError(error, t.auth.resetPassword.sendCodeFailedRetry);
        setErrorMessage(errorMsg);
        setSendingCode(false);
        return;
      }

      message.success(t.auth.resetPassword.codeSent);
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      const errorMsg = handleApiError(error, t.auth.resetPassword.sendCodeFailedRetry);
      setErrorMessage(errorMsg);
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      console.log('尝试重置密码:', { email });
      
      const response = await fetch('https://api.kalimbaflows.com/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, newPassword: password, code }),
      });

      const data = await response.json();
      console.log('重置密码响应:', data);

      if (!response.ok) {
        const error = new Error(data.message || t.auth.resetPassword.resetFailed);
        const errorMsg = handleApiError(error, t.auth.resetPassword.resetFailedCheckInput);
        setErrorMessage(errorMsg);
        setLoading(false);
        return;
      }

      message.success(t.auth.resetPassword.resetSuccess);
      navigate('/login', { replace: true });
    } catch (error) {
      const errorMsg = handleApiError(error, t.auth.resetPassword.resetFailedNetwork);
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        <PageTitle>{t.auth.resetPassword.title}</PageTitle>
        <FormContainer onSubmit={handleSubmit}>
          <StyledInput
            placeholder={t.auth.resetPassword.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
          <StyledPassword
            placeholder={t.auth.resetPassword.newPasswordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <CodeInputContainer>
            <StyledInput
              placeholder={t.auth.resetPassword.codePlaceholder}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <CodeButton
              onClick={handleSendCode}
              disabled={countdown > 0}
              loading={sendingCode}
            >
              {countdown > 0 ? `${countdown}s` : t.auth.resetPassword.sendCode}
            </CodeButton>
          </CodeInputContainer>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <Button type="primary" htmlType="submit" loading={loading} block>
            {t.auth.resetPassword.resetButton}
          </Button>
          <LinksContainer>
            <StyledLink to="/login">{t.auth.resetPassword.backToLogin}</StyledLink>
          </LinksContainer>
        </FormContainer>
      </Card>
    </PageContainer>
  );
}

export default ResetPassword; 